<template>
    <div
        id="summarize-patient-profile-modal"
        ref="modal"
        :style="{ top: `${position.top}px`, left: `${position.left}px` }"
    >
        <div class="options-overlay" v-if="isShowOptions">
            <div class="options-content">
                <h1>OPTIONS [DEV ONLY]</h1>
                <div class="item">
                    <div class="label">Model</div>
                    <span class="description">
                        Select the LLM model to generate/process responses
                    </span>
                    <div>
                        <select v-model="tempOptions.model">
                            <option value="CLAUDE_3_OPUS">Claude 3 Opus</option>
                            <option value="CLAUDE_35_SONNET">
                                Claude 3.5 Sonnet
                            </option>
                            <option value="CLAUDE_35_HAIKU">
                                Claude 3.5 Haiku
                            </option>
                            <option value="CLAUDE_3_HAIKU">
                                Claude 3 Haiku
                            </option>
                        </select>
                    </div>
                </div>
                <div class="item">
                    <div class="label">
                        Initial Prompt
                    </div>
                    <div>
                        <textarea v-model="tempOptions.intialPrompt"></textarea>
                    </div>
                </div>
                <div class="item">
                    <div class="label">
                        <label>
                            <input
                                type="checkbox"
                                v-model="tempOptions.autoInitPrompt"
                            />
                            Auto-init prompt
                        </label>
                    </div>
                    <span class="description">
                        Automatically initialize the prompt when the modal is
                        opened. Restart the modal to see the effect.
                    </span>
                </div>
                <div class="item">
                    <div class="label">
                        <label>
                            <input
                                type="checkbox"
                                v-model="tempOptions.showInitialPrompt"
                            />
                            Show initial prompt
                        </label>
                    </div>
                    <span class="description">
                        Display the user’s first message.
                    </span>
                </div>
                <div class="item">
                    <div class="label">
                        <label>
                            <input
                                type="checkbox"
                                v-model="tempOptions.suggestedQuestions"
                            />
                            Suggested questions
                        </label>
                    </div>
                    <span class="description">
                        Request the AI to generate 5 follow-up questions.
                    </span>
                </div>
                <div class="item">
                    <div class="label">
                        <label>
                            <input
                                type="checkbox"
                                v-model="tempOptions.showUserInput"
                            />
                            Show user input
                        </label>
                    </div>
                    <span class="description">
                        Toggle the visibility of the textarea for open-ended
                        interactions/chat.
                    </span>
                </div>

                <div class="actions">
                    <button @click="cancelOptions">Cancel</button>
                    <button @click="resetOptions">Reset</button>
                    <button @click="saveOptions">Save</button>
                </div>
            </div>
        </div>

        <div class="header" @mousedown="startDrag">
            <div class="left">
                <h1>Patient Profile Summary</h1>
                <div v-show="isLoadingAudio" class="loaderAudio"></div>
                <button
                    class="stop"
                    :class="{ show: audioContext }"
                    @click="stopAloud"
                >
                    <i class="far fa-stop-circle"></i>
                </button>
            </div>
            <div class="right">
                <button class="options" @click="isShowOptions = true">
                    <i class="fas fa-cog" />
                </button>
                <toggle-input
                    id="summary-auto-read-aloud"
                    label="Auto-read aloud"
                    v-model="autoReadAloud"
                    @change="toggleAutoRead"
                />
                <button class="close" @click="close">
                    <i class="fas fa-times" />
                </button>
            </div>
        </div>

        <div class="content" ref="content">
            <div v-for="(message, index) in filteredMessages" :key="index">
                <div
                    v-if="shouldDisplayUserMessage(message, index)"
                    class="message user"
                >
                    <div class="text">
                        {{ showUserMessage(message.content[0].text) }}
                    </div>
                </div>

                <div
                    :key="message.timestamp"
                    v-else-if="
                        message.role === 'assistant' &&
                        message.uiType !== 'questions_list'
                    "
                    class="assistant-msg-wrapper"
                >
                    <!-- <div class="speaking-indicator"></div> -->

                    <vue-markdown class="message assistant">
                        {{ message.content[0].text }}
                    </vue-markdown>
                    <div
                        class="audio-controller"
                        v-show="!isProcessing && !isTyping && !isLoading"
                    >
                        <button
                            disabled
                            v-if="
                                isLoadingAudio && audioMessageId === message.id
                            "
                        >
                            <div class="loaderAudio"></div>
                        </button>
                        <button
                            v-else-if="
                                audioContext && audioMessageId === message.id
                            "
                            @click="stopAloud"
                        >
                            <i class="fas fa-stop-circle"></i>
                        </button>
                        <button v-else @click="playAloud(message.id)">
                            <i class="far fa-play-circle"></i>
                        </button>
                    </div>
                </div>

                <div
                    v-else-if="message.uiType === 'questions_list'"
                    class="questions"
                >
                    <div>Suggested questions:</div>
                    <ul>
                        <li
                            v-for="(question, index2) in message.questions"
                            :key="index2"
                        >
                            <a
                                href="#"
                                @click.prevent="followUpQuestion(question)"
                            >
                                {{ question }}
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
            <div v-if="isLoading" class="loader"></div>
            <div v-else-if="isProcessing" class="loader processing"></div>
        </div>

        <div class="footer" v-show="options.showUserInput">
            <div v-if="isLoading || isTyping" class="overlay"></div>
            <div class="input-wrapper">
                <textarea
                    placeholder="Ask anything about this patient"
                    ref="userinput"
                    v-model="query"
                    @keydown="handleKeydown"
                    :disabled="isLoading || isTyping || isProcessing"
                ></textarea>
                <button
                    @click="sendMessage"
                    :disabled="isLoading || isTyping || isProcessing"
                >
                    <i class="far fa-paper-plane"></i>
                </button>
            </div>
            <!-- <div
                v-for="(query, index) in presetSummary"
                :key="index"
                class="suggested-summary"
                @click="followUpQuestion(query)"
            >
                {{ query }}
            </div> -->
        </div>
    </div>
</template>

<script>
import ToggleInput from "@/components/commons/toggle-input.vue";
import VueMarkdown from "vue-markdown";

const INITIAL_PROMPT =
    "In one paragraph, generate a simple brief profile for this patient.";

const USER = "user";
const ASSISTANT = "assistant";
const OPTIONSKEY = "summarizerOptions";

const OPTIONS = {
    model: "CLAUDE_35_SONNET", // CLAUDE_35_SONNET | CLAUDE_35_HAIKU | CLAUDE_3_OPUS
    intialPrompt: INITIAL_PROMPT,
    autoInitPrompt: true,
    showInitialPrompt: false,
    suggestedQuestions: false,
    showUserInput: true
};

function getOptions() {
    let raw = localStorage.getItem(OPTIONSKEY);
    try {
        if (!raw) {
            return { ...OPTIONS };
        }
        return JSON.parse(raw);
    } catch {
        return { ...OPTIONS };
    }
}

export default {
    components: { VueMarkdown, ToggleInput },
    props: {
        patientId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            messages: [],
            query: "",
            isLoading: false,
            isTyping: false,
            isProcessing: false,
            presetSummary: [
                "Boarding Summary",
                "Visit & Appointment Summary",
                "Diagnosis Summary",
                "Medical Record Summary",
                "Profile Summary"
            ],
            audioContext: null,
            audioSource: null,
            isLoadingAudio: null,
            audioMessageId: null,
            audioBuffer: [],
            autoReadAloud: localStorage.getItem("autoReadAloud") === "true",
            position: {
                top: 100,
                left: (window.innerWidth - 600) / 2 // center horizontally based on width
            },
            mouseOffset: { x: 0, y: 0 },
            dragging: false,
            modalDimensions: { width: 0, height: 0 },
            options: getOptions(),
            tempOptions: getOptions(),
            isShowOptions: false
        };
    },
    computed: {
        MSG_PREFIX() {
            return `[Patient ID: ${this.patientId}]`;
        },
        filteredMessages() {
            return this.messages.filter(message => !message.hidden);
        }
    },
    async mounted() {
        if (this.options?.autoInitPrompt) {
            this.initPrompt();
        }

        await this.$nextTick();
        this.$refs.userinput?.focus();

        // cache the dimensions once the component is mounted
        const modal = this.$refs.modal;
        this.modalDimensions.width = modal.offsetWidth;
        this.modalDimensions.height = modal.offsetHeight;
    },
    methods: {
        shouldDisplayUserMessage(message, index) {
            if (message.role !== "user") return false;
            if (index === 0) return this.options.showInitialPrompt;
            return true;
        },
        saveOptions() {
            let options = { ...this.tempOptions };
            localStorage.setItem(OPTIONSKEY, JSON.stringify(options));
            this.options = { ...options };
            this.tempOptions = { ...options };
            this.isShowOptions = false;
        },
        cancelOptions() {
            this.isShowOptions = false;
            this.tempOptions = getOptions();
        },
        resetOptions() {
            this.tempOptions = { ...OPTIONS };
        },
        handleKeydown(event) {
            if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                this.sendMessage();
            }
        },
        sendMessage() {
            if (this.query.trim()) {
                this.promptQuestion(this.query);
                this.query = "";
            }
        },
        toggleAutoRead(newValue) {
            localStorage.setItem("autoReadAloud", newValue);
        },
        async playAloudFromMemory(messageId) {
            this.stopAloud(); // Stop any currently playing audio
            this.audioMessageId = messageId;
            this.audioContext = new AudioContext();
            this.audioSource = this.audioContext.createBufferSource();
            this.audioSource.buffer = this.audioBuffer[messageId]; // Play from memory
            // this.audioSource.playbackRate.value = 1.1; // makin tinggi value, makin laju dia cakap
            this.audioSource.connect(this.audioContext.destination);

            this.audioSource.onended = () => {
                this.stopAloud();
            };

            this.audioSource.start();
            setTimeout(() => {
                this.isLoadingAudio = false;
            }, 800);
        },
        async playAloud(messageId) {
            // Check if audio is already in memory
            if (this.audioBuffer[messageId]) {
                this.playAloudFromMemory(messageId);
                return;
            }

            this.stopAloud();

            let msg = this.messages.find(m => m.id === messageId);
            let speechText = msg?.content?.[0].text;

            if (!speechText || speechText === "") {
                return;
            }

            const headers = {
                "Content-Type": "application/json",
                "x-api-key": "HailHydra",
                "vetter-token": localStorage.getItem("Vetter-Token")
            };
            const body = JSON.stringify({
                text: speechText
            });

            this.audioMessageId = messageId;
            this.isLoadingAudio = true;

            const response = await fetch(`${process.env.AI_BASE_URL}speech`, {
                method: "POST",
                headers,
                body
            });

            const arrayBuffer = await response.arrayBuffer();
            const audioContext = new AudioContext();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            this.audioBuffer[messageId] = audioBuffer;
            this.playAloudFromMemory(messageId);
        },
        stopAloud() {
            this.audioMessageId = null;

            if (this.audioSource) {
                this.audioSource.stop();
                this.audioSource = null;
            }

            if (this.audioContext) {
                this.audioContext.close();
                this.audioContext = null;
            }
        },
        async followUpQuestion(question) {
            this.messages.splice(-2, 2);
            await this.$nextTick();
            this.scrollToBottom();
            await this.promptQuestion(question);
        },
        showUserMessage(msg) {
            return msg.split("] ")[1] || msg;
        },
        scrollToBottom() {
            this.$refs.content?.scrollTo(0, this.$refs.content.scrollHeight);
        },
        close() {
            this.stopAloud();
            this.$emit("close"); // close and clear memory
        },
        addMessage(role, msg, extra = {}) {
            if (!extra.uiType) {
                this.stopAloud();
            }

            this.messages.push({
                id: Date.now(),
                role,
                content: [{ text: msg }],
                ...extra
            });
        },
        async promptQuestion(question) {
            this.addMessage(USER, `${this.MSG_PREFIX} ${question}`);

            await this.nextStep();
        },
        async nextStep(isFollowUpQuestions = false) {
            await this.$nextTick();
            this.scrollToBottom();
            const response = await this.promptRequest();
            if (response.ok) {
                await this.processResponses(response, isFollowUpQuestions);
            } else {
                this.handleError(response);
            }
        },
        async promptRequest() {
            this.isLoading = true;

            await this.$nextTick();
            this.scrollToBottom();

            let messages = [];

            for (let n = 0; n < this.messages.length; n++) {
                let msg = this.messages[n];
                let prevMsg = this.messages[n - 1];

                if (
                    prevMsg?.role === ASSISTANT &&
                    prevMsg?.content?.[0]?.text === ""
                ) {
                    messages.splice(-2, 2);
                }

                let newMsg = JSON.parse(JSON.stringify(msg));
                delete newMsg.id;
                delete newMsg.timestamp;
                delete newMsg.hidden;
                delete newMsg.uiType;
                delete newMsg.questions;
                messages.push(newMsg);
            }

            const body = JSON.stringify({
                patient_id: this.patientId,
                collection_msg: messages,
                model: this.options.model
            });

            const res = await fetch(`${process.env.AI_BASE_URL}summarize-ai`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-api-key": "HailHydra",
                    "vetter-token": localStorage.getItem("Vetter-Token"),
                    Accept: "text/event-stream"
                },
                body
            });

            this.isLoading = false;
            return res;
        },
        async processResponses(response, isFollowUpQuestions = false) {
            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let done = false;

            const messageId = Date.now();

            const assistantMessage = {
                id: messageId,
                role: ASSISTANT,
                content: [{ text: "" }],
                timestamp: Date.now()
            };

            if (isFollowUpQuestions) {
                assistantMessage.uiType = "questions_list";
            }

            this.messages.push(assistantMessage);
            this.scrollToBottom();

            const appendText = text => {
                assistantMessage.content[0].text += text;
                assistantMessage.timestamp += 1;
                this.scrollToBottom();
            };

            this.isTyping = true;

            let questionsArrStr = "";

            while (!done) {
                const { value, done: streamDone } = await reader.read();
                done = streamDone;

                if (value) {
                    const chunk = decoder.decode(value, { stream: true });
                    const splitted = chunk.split("\n\n");

                    for (let event of splitted) {
                        if (event.startsWith("event: tool_use")) {
                            this.isProcessing = true;
                            continue;
                        }

                        const match = event.match(/^data: (.+)$/);
                        if (match) {
                            if (this.isProcessing) {
                                this.isProcessing = false;
                            }

                            try {
                                const parsedData = JSON.parse(match[1]);

                                if (parsedData.text) {
                                    if (
                                        [
                                            "tool_use",
                                            "tool_",
                                            "_use",
                                            "\n\ntool",
                                            "\ntool"
                                        ].includes(parsedData.text)
                                    ) {
                                        continue;
                                    }

                                    if (isFollowUpQuestions) {
                                        questionsArrStr += parsedData.text;
                                    } else {
                                        appendText(parsedData.text);
                                    }
                                }
                            } catch {
                                appendText("Error parsing response.");
                                this.isTyping = false;
                            }
                        }
                    }
                }
            }

            if (this.autoReadAloud) {
                this.playAloud(messageId);
            }

            if (isFollowUpQuestions) {
                try {
                    let json = JSON.parse(questionsArrStr);
                    assistantMessage.questions = json;
                } catch (err) {
                    console.error(err.message);
                    this.messages.splice(-2, 2);
                    await this.$nextTick();
                }
            }

            // follow up questions
            if (
                this.options.suggestedQuestions &&
                !isFollowUpQuestions &&
                assistantMessage.content[0].text !== ""
            ) {
                this.addMessage(
                    USER,
                    // "Give me 5 very short follow-up questions in JSON array. Without starting with 'Here are 5 follow-up questions:'.",
                    "In plain JSON array string without codeblock, list 5 follow-up questions, short one-liners, to ask you again. Without starting with 'Here are 5 follow-up questions'",
                    {
                        hidden: true,
                        uiType: "question_prompt"
                    }
                );
                await this.nextStep(true);
            } else {
                this.isTyping = false;
            }

            await this.$nextTick();
            this.$refs.userinput?.focus();
            this.scrollToBottom();
        },
        handleError(response) {
            this.isLoading = false;
            this.isTyping = false;
            this.addMessage(
                ASSISTANT,
                `Error: ${response.status} ${response.statusText}`
            );
            this.$nextTick(() => {
                this.scrollToBottom();
            });
        },
        async initPrompt() {
            this.messages = [];
            let prompt = this.options.intialPrompt ?? INITIAL_PROMPT;
            this.addMessage(USER, `${this.MSG_PREFIX} ${prompt}`);
            await this.nextStep();
        },
        startDrag(event) {
            this.dragging = true;
            this.mouseOffset.x = event.clientX - this.position.left;
            this.mouseOffset.y = event.clientY - this.position.top;

            document.addEventListener("mousemove", this.onDrag);
            document.addEventListener("mouseup", this.stopDrag);
        },
        onDrag(event) {
            if (!this.dragging) return;

            const newLeft = event.clientX - this.mouseOffset.x;
            const newTop = event.clientY - this.mouseOffset.y;

            const constrainedLeft = Math.min(
                Math.max(newLeft, 0),
                window.innerWidth - this.modalDimensions.width
            );
            const constrainedTop = Math.min(
                Math.max(newTop, 0),
                window.innerHeight - this.modalDimensions.height
            );

            this.position.left = constrainedLeft;
            this.position.top = constrainedTop;
        },
        stopDrag() {
            this.dragging = false;
            document.removeEventListener("mousemove", this.onDrag);
            document.removeEventListener("mouseup", this.stopDrag);
        }
    }
};
</script>

<style lang="scss" scoped>
#summarize-patient-profile-modal {
    position: fixed;
    top: 100px;
    width: 600px;
    height: 700px;
    background: white;
    border-radius: 14px;
    box-shadow: 0px 10px 15px 0px #6d6d6d;
    border: 1px solid #e2e2e2;
    display: flex;
    flex-direction: column;
    z-index: 1020;

    .options-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba($color: #000, $alpha: 0.5);
        z-index: 1;
        border-radius: inherit;
        display: flex;
        align-items: center;
        justify-content: center;

        .options-content {
            width: 80%;
            height: 80%;
            background: white;
            padding: 15px;
            user-select: none;
            overflow-y: auto;

            h1 {
                font-size: 1em;
                padding-bottom: 3px;
                background: #e1e1e1;
                margin: 0;
                padding: 3px;
                margin-bottom: 10px;
            }

            .item {
                margin-bottom: 20px;
            }

            .label {
                font-weight: 600;
                margin-bottom: 3px;

                label {
                    margin: 0;
                    padding: 0;
                    display: flex;
                    align-items: center;
                    cursor: pointer;

                    input {
                        margin-right: 5px;
                    }
                }
            }
            .description {
                font-size: 0.9em;
                margin-bottom: 5px;
                display: block;
            }

            select,
            textarea {
                width: 100%;
            }

            .actions {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-top: 30px;
            }
        }
    }

    .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 20px;
        border-bottom: 1px solid #e2e2e2;
        user-select: none;

        .left {
            display: flex;
            align-items: center;
            gap: 7px;

            h1 {
                margin: 0;
                font-size: 1.2em;
            }

            .loaderAudio {
                opacity: 0.5;
            }

            button.stop {
                outline: none;
                visibility: hidden;
                border: none;
                background: none;
                font-size: 1.5em;
                padding: 0;
                margin: 0;
                color: #bf0000;

                &:hover {
                    color: #f02a2a;
                }

                &.show {
                    visibility: visible;
                }
            }
        }

        .right {
            display: flex;
            gap: 10px;
            user-select: none;
            align-items: center;

            button {
                background: none;
                border: none;
                font-size: 1.4em;
                outline: none;
                color: gray;
            }

            button.close {
                margin-left: 15px;
            }

            button.options {
                font-size: 1.2em;
                opacity: 0.7;

                &:hover {
                    opacity: 1;
                }
            }
        }
    }

    .content {
        flex: 1;
        overflow-y: auto;
        padding: 15px 20px;
        line-height: 1.6em;
        display: flex;
        flex-direction: column;
        gap: 15px;
        scroll-behavior: smooth;
        background: #fbfbfb;

        .audio-controller {
            display: flex;
            margin-top: 3px;
            visibility: hidden;

            button {
                background: none;
                border: none;
                font-size: 1.3em;
                color: #767676;
                padding: 5px;
                margin: 0;
                outline: none;
                border-radius: 7px;

                &:hover {
                    color: #353535;
                    background: #ececec;
                }
            }
        }

        .assistant-msg-wrapper {
            position: relative;

            &:hover {
                .audio-controller {
                    visibility: visible;
                }
            }

            .speaking-indicator {
                display: inline-grid;
                font-size: 30px;
                position: absolute;
                top: 1px;
                right: 10px;
                opacity: 0.5;
                transform: scaleX(-1);
            }
            .speaking-indicator:before,
            .speaking-indicator:after {
                content: "🔊";
                grid-area: 1/1;
            }
            .speaking-indicator:after {
                animation: speakingIndicator 1s infinite;
            }
            @keyframes speakingIndicator {
                to {
                    transform: scale(1.8);
                    opacity: 0;
                }
            }
        }

        ::v-deep .message.assistant {
            h1 {
                font-size: 1.5em;
            }
            h2 {
                font-size: 1.4em;
            }
            h3 {
                font-size: 1.3em;
            }
            h4 {
                font-size: 1.2em;
            }
            h5 {
                font-size: 1.1em;
            }
            h5 {
                font-size: 1em;
            }
            p:last-of-type {
                margin-bottom: 0;
            }
        }

        .message.user {
            display: flex;
            justify-content: flex-end;

            .text {
                background: #ebf0e5;
                max-width: 65%;
                border-radius: 5px;
                padding: 3px 12px;
                text-align: left;
            }
        }

        .questions {
            padding: 15px;
            border: 1px solid #bdbdbd;
            border-radius: 4px;

            ul {
                margin: 0;
            }

            li {
                opacity: 0;
                transform: translateY(20px);
                animation: fadeSlideUp 0.5s ease-out forwards;
            }

            li:nth-child(1) {
                animation-delay: 0.1s;
            }
            li:nth-child(2) {
                animation-delay: 0.2s;
            }
            li:nth-child(3) {
                animation-delay: 0.3s;
            }
            li:nth-child(4) {
                animation-delay: 0.4s;
            }
            li:nth-child(5) {
                animation-delay: 0.5s;
            }

            @keyframes fadeSlideUp {
                0% {
                    opacity: 0;
                    transform: translateY(20px);
                }
                100% {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
        }
    }

    .footer {
        padding: 10px 20px;
        min-height: 80px;
        border-top: 1px solid #e2e2e2;
        display: flex;
        flex-wrap: wrap;
        gap: 7px;
        justify-content: center;
        position: relative;
        background: #f9f9f9;
        border-radius: inherit;
        border-top-right-radius: 0;
        border-top-left-radius: 0;

        .input-wrapper {
            flex: 1;
            display: flex;
            background: white;
            padding: 7px;
            // box-shadow: 0px 2px 2px #cccccc;
            border-radius: 7px;
            border: 1px solid #bebebe;

            textarea {
                flex: 1;
                resize: none;
                outline: none;
                border: none;
                background: none;
            }

            button {
                width: 45px;
                border: none;
                background: transparent;
                color: #386e9d;
                font-size: 1.3em;
                border-radius: 12px;
                align-content: center;
                display: flex;
                align-items: center;
                justify-content: center;

                &:hover {
                    background: #dceaf6;
                    color: #333;
                }
            }
        }

        .overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            cursor: not-allowed;
            background: rgba($color: #fff, $alpha: 0.4);
        }

        .suggested-summary {
            background-color: #f0f0f0;
            display: flex;
            justify-content: center;
            align-items: center;
            box-sizing: border-box;
            padding: 0 10px;
            border-radius: 10px;
            cursor: pointer;
            user-select: none;

            &:hover {
                background: #d8ddf7;
                color: black;
            }
        }
    }

    .loader {
        width: 35px;
        aspect-ratio: 2;
        --_g: no-repeat radial-gradient(circle closest-side, #55799c 90%, #0000);
        background: var(--_g) 0% 50%, var(--_g) 50% 50%, var(--_g) 100% 50%;
        background-size: calc(100% / 3) 50%;
        animation: l3 1s infinite linear;

        &.processing {
            --_g: no-repeat
                radial-gradient(circle closest-side, #468643 90%, #0000);
        }
    }
    @keyframes l3 {
        20% {
            background-position: 0% 0%, 50% 50%, 100% 50%;
        }
        40% {
            background-position: 0% 100%, 50% 0%, 100% 50%;
        }
        60% {
            background-position: 0% 50%, 50% 100%, 100% 0%;
        }
        80% {
            background-position: 0% 50%, 50% 50%, 100% 100%;
        }
    }

    .loader2 {
        width: 15px;
        aspect-ratio: 1;
        border-radius: 50%;
        background: #000;
        box-shadow: 0 0 0 0 #0004;
        animation: l1 1s infinite;
    }
    @keyframes l1 {
        100% {
            box-shadow: 0 0 0 30px #0000;
        }
    }

    .loaderAudio {
        width: 16px;
        aspect-ratio: 1;
        --_g: no-repeat radial-gradient(farthest-side, #3f3f3f 94%, #0000);
        background: var(--_g) 0 0, var(--_g) 100% 0, var(--_g) 100% 100%,
            var(--_g) 0 100%;
        background-size: 40% 40%;
        animation: l38 0.5s infinite;
    }
    @keyframes l38 {
        100% {
            background-position: 100% 0, 100% 100%, 0 100%, 0 0;
        }
    }
}
</style>
